import { PieceCategory } from '@activepieces/shared';

export const KHUB_TOOLS_CATEGORY = 'KHUB_TOOLS';
export const OTHER_CATEGORY = 'OTHER';

type KhubToolPieceCategory =
  | PieceCategory.CORE
  | PieceCategory.FLOW_CONTROL
  | PieceCategory.UNIVERSAL_AI;

type BusinessPieceCategory = Exclude<
  PieceCategory,
  KhubToolPieceCategory | PieceCategory.SWAGGER
>;

export type CatalogCategory =
  | BusinessPieceCategory
  | typeof KHUB_TOOLS_CATEGORY
  | typeof OTHER_CATEGORY;

export type CatalogPiece = {
  name: string;
  displayName: string;
  description: string;
  categories?: PieceCategory[];
};

const KHUB_TOOL_PIECE_CATEGORIES: ReadonlySet<PieceCategory> = new Set([
  PieceCategory.CORE,
  PieceCategory.FLOW_CONTROL,
  PieceCategory.UNIVERSAL_AI,
]);

const TRAILING_CATEGORIES: CatalogCategory[] = [
  KHUB_TOOLS_CATEGORY,
  OTHER_CATEGORY,
];

const isKhubToolPieceCategory = (
  category: PieceCategory,
): category is KhubToolPieceCategory =>
  KHUB_TOOL_PIECE_CATEGORIES.has(category);

// Swagger pieces belong to a single client and have their own admin section.
export const catalogCategoriesOf = (
  piece: Pick<CatalogPiece, 'categories'>,
): CatalogCategory[] => {
  const catalogCategories = new Set<CatalogCategory>();
  for (const category of piece.categories ?? []) {
    if (category === PieceCategory.SWAGGER) {
      continue;
    }
    catalogCategories.add(
      isKhubToolPieceCategory(category) ? KHUB_TOOLS_CATEGORY : category,
    );
  }
  return catalogCategories.size === 0
    ? [OTHER_CATEGORY]
    : Array.from(catalogCategories);
};

export const countByCatalogCategory = (
  pieces: Pick<CatalogPiece, 'categories'>[],
): Map<CatalogCategory, number> => {
  const counts = new Map<CatalogCategory, number>();
  for (const piece of pieces) {
    for (const category of catalogCategoriesOf(piece)) {
      counts.set(category, (counts.get(category) ?? 0) + 1);
    }
  }
  return counts;
};

// Ordered on the whole list, not the searched one, so entries keep their place
// while the user types.
export const orderCatalogCategories = (
  pieces: Pick<CatalogPiece, 'categories'>[],
): CatalogCategory[] => {
  const counts = countByCatalogCategory(pieces);
  const countOf = (category: CatalogCategory): number =>
    counts.get(category) ?? 0;
  const businessCategories = Array.from(counts.keys())
    .filter((category) => !TRAILING_CATEGORIES.includes(category))
    .sort((a, b) => countOf(b) - countOf(a) || a.localeCompare(b));
  return [
    ...businessCategories,
    ...TRAILING_CATEGORIES.filter((category) => counts.has(category)),
  ];
};

// Derived from the mapping itself so the accepted URL values cannot drift from it.
const CATALOG_CATEGORIES: ReadonlySet<string> = new Set(
  Object.values(PieceCategory).flatMap((category) =>
    catalogCategoriesOf({ categories: [category] }),
  ),
);

const isCatalogCategory = (value: string): value is CatalogCategory =>
  CATALOG_CATEGORIES.has(value);

export const parseCatalogCategory = (
  value: string | null,
): CatalogCategory | null =>
  value !== null && isCatalogCategory(value) ? value : null;
